import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../App/store";

export interface CartItem {
  productID: string;
  productName: string;
  productPrice: number;
  productReviews: string;
  frontImg: string;
  backImg?: string;
  productDiscount?: number;
  quantity: number;
}

interface CartState {
  items: CartItem[];
  totalAmount: number;
}

const initialState: CartState = {
  items: [],
  totalAmount: 0,
};

const MAX_QUANTITY = 20;

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart(state, action: PayloadAction<Omit<CartItem, "quantity">>) {
      const product = action.payload;
      const existingItem = state.items.find(
        (item) => item.productID === product.productID
      );
      if (existingItem) {
        if (existingItem.quantity < MAX_QUANTITY) {
          existingItem.quantity += 1;
          state.totalAmount += product.productPrice;
        }
      } else {
        state.items.push({ ...product, quantity: 1 });
        state.totalAmount += product.productPrice;
      }
    },

    updateQuantity(
      state,
      action: PayloadAction<{ productID: string; quantity: number }>
    ) {
      const { productID, quantity } = action.payload;
      const itemToUpdate = state.items.find(
        (item) => item.productID === productID
      );
      if (itemToUpdate) {
        const difference = quantity - itemToUpdate.quantity;
        if (quantity <= MAX_QUANTITY) {
          itemToUpdate.quantity = quantity;
          state.totalAmount += difference * itemToUpdate.productPrice;
        } else {
          const adjustedDifference = MAX_QUANTITY - itemToUpdate.quantity;
          itemToUpdate.quantity = MAX_QUANTITY;
          state.totalAmount += adjustedDifference * itemToUpdate.productPrice;
        }
      }
    },

    removeFromCart(state, action: PayloadAction<string>) {
      const productID = action.payload;
      const itemToRemove = state.items.find(
        (item) => item.productID === productID
      );
      if (itemToRemove) {
        state.totalAmount -= itemToRemove.productPrice * itemToRemove.quantity;
        state.items = state.items.filter((item) => item.productID !== productID);
      }
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity } =
  cartSlice.actions;

// Selectors with RootState type


export const selectCartItems = (state: RootState) => state.cart.items;
export const selectCartTotalAmount = (state: RootState) => state.cart.totalAmount;

export default cartSlice.reducer;
